import { BasePayloadMapper } from '@ps/utils';
import {
  AdvancedTargetingDefinition,
  SurveyMetadataForm,
  SurveyQualificationTab,
} from '@ps/survey-utils';

import { QualificationPayloadDto } from '../dtos/survey-qualification.dto';
import { QuotaPayloadDto } from '../dtos/survey-quota.dto';
import { StandardSurveyPayloadDto } from '../dtos/survey.dto';
import { SURVEY_CATEGORY_CODE } from '../../utils';
import { QuotaPayloadMapper } from './quota.payload.mapper';

export interface StandardSurveyTargetingInput {
  definitions: AdvancedTargetingDefinition[];
  locationSelectionsByQualificationCode: Record<number, string[]>;
  qualifications: SurveyQualificationTab[];
}

export interface StandardSurveyPayloadInput {
  metadata: SurveyMetadataForm;
  companyId: number;
  userId: number;
  targeting?: StandardSurveyTargetingInput;
}

export class StandardSurveyPayloadMapper extends BasePayloadMapper<
  StandardSurveyPayloadInput,
  StandardSurveyPayloadDto
> {
  private readonly _quotaMapper = new QuotaPayloadMapper();

  public toPayload({
    metadata,
    companyId,
    userId,
    targeting,
  }: StandardSurveyPayloadInput): StandardSurveyPayloadDto {
    const payload: StandardSurveyPayloadDto = {
      survey_title: metadata.title,
      buyer_company: companyId,
      buyer_user_id: userId,
      survey_category_code: SURVEY_CATEGORY_CODE,
      survey_localization: this._localization(metadata),
      completes_required: metadata.completes ?? 0,
      expected_loi: metadata.loi ?? 0,
      expected_ir: metadata.incidence ?? 0,
      field_time: metadata.fieldTime ?? 0,
      click_balancing: this._clickBalancing(metadata),
    };

    const qualifications = this._qualifications(targeting);
    const quotas = this._quotas(targeting);

    return {
      ...payload,
      ...(qualifications.length ? { qualifications } : {}),
      ...(quotas.length ? { quotas } : {}),
    };
  }

  private _localization(metadata: SurveyMetadataForm): string {
    const countryCode = metadata.selectedCountries[0];
    const languageCode = metadata.selectedLanguages[0];
    if (!countryCode || !languageCode) {
      return '';
    }
    return `${languageCode}_${countryCode}`;
  }

  private _clickBalancing(metadata: SurveyMetadataForm): number {
    return metadata.clickBalancing ? 1 : 0;
  }

  // Advance Targeting entries precede Location entries, matching the order the
  // legacy payload builder produced.
  private _qualifications(
    targeting: StandardSurveyTargetingInput | undefined,
  ): QualificationPayloadDto[] {
    if (!targeting) {
      return [];
    }

    return [
      ...targeting.definitions.map((definition) =>
        this._advancedTargetingQualification(definition),
      ),
      ...this._locationQualifications(targeting),
    ];
  }

  private _advancedTargetingQualification(
    definition: AdvancedTargetingDefinition,
  ): QualificationPayloadDto {
    const payload: QualificationPayloadDto = {
      qualification_name: definition.title,
      qualification_code: Number(definition.attributeId),
      condition_codes: definition.selectedOptionIds,
    };

    // A range definition can only have been hydrated from a saved survey — the
    // drawer cannot author one. Re-emitting its range set keeps the PUT, which
    // replaces qualifications wholesale, from deleting it.
    if (!definition.rangeSet) {
      return payload;
    }

    return { ...payload, range_sets: [definition.rangeSet] };
  }

  // Sibling of qualifications, not nested inside it. Recomputed from every definition's
  // applied quotas each save, matching qualifications' whole-array-replace semantics.
  private _quotas(
    targeting: StandardSurveyTargetingInput | undefined,
  ): QuotaPayloadDto[] {
    if (!targeting) {
      return [];
    }

    return targeting.definitions
      .flatMap((definition) => definition.quotas ?? [])
      .map((quota) => this._quotaMapper.toPayload(quota));
  }

  private _locationQualifications({
    locationSelectionsByQualificationCode,
    qualifications,
  }: StandardSurveyTargetingInput): QualificationPayloadDto[] {
    return qualifications
      .filter((qualification) => qualification.isLocation)
      .map((qualification) => ({
        qualification,
        selectedOptionIds:
          locationSelectionsByQualificationCode[
            qualification.qualificationCode
          ] ?? [],
      }))
      .filter(({ selectedOptionIds }) => selectedOptionIds.length > 0)
      .map(({ qualification, selectedOptionIds }) => ({
        qualification_name: qualification.label,
        qualification_code: qualification.qualificationCode,
        condition_codes: selectedOptionIds,
      }));
  }
}
